import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight, AlertCircle, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import PolicyModal from '../components/PolicyModal';
import { checkRateLimit, recordFailedAttempt, recordSuccessfulAttempt, getRateLimitMessage } from '../utils/rateLimiter';

const AuthLanding: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyModalType, setPolicyModalType] = useState<'privacy' | 'terms'>('terms');
  const [isOAuthCallback, setIsOAuthCallback] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong' | null>(null);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp, user, loading, resetPassword } = useAuth();

  // Ref to prevent infinite loop when clearing stale sessions
  const isSigningOut = useRef(false);
  // Ref to track if this is an OAuth callback (persists even after URL hash is cleared)
  const isOAuthCallbackRef = useRef(false);

  // Get the redirect URL from state or default to shop-picks
  const from = location.state?.from?.pathname || '/shop-picks';

  // SECURITY: Validate password strength
  const validatePassword = (password: string): { isValid: boolean; message?: string } => {
    if (password.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters long' };
    }

    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

    if (!hasUppercase) {
      return { isValid: false, message: 'Password must contain at least one uppercase letter' };
    }
    if (!hasLowercase) {
      return { isValid: false, message: 'Password must contain at least one lowercase letter' };
    }
    if (!hasNumber) {
      return { isValid: false, message: 'Password must contain at least one number' };
    }
    if (!hasSpecialChar) {
      return { isValid: false, message: 'Password must contain at least one special character (!@#$%^&*...)' };
    }

    return { isValid: true };
  };

  // Calculate password strength for UI indicator
  const calculatePasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
    if (password.length < 8) return 'weak';

    let strength = 0;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) strength++;
    if (password.length >= 12) strength++;

    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  };

  // Update password strength indicator when password changes
  useEffect(() => {
    if (!isLogin && password) {
      setPasswordStrength(calculatePasswordStrength(password));
    } else {
      setPasswordStrength(null);
    }
  }, [password, isLogin]);

  // SECURITY: Clear any stale sessions when auth page loads
  // This prevents auto-login from old sessions when user explicitly visits /auth
  useEffect(() => {
    const clearStaleSession = async () => {
      // Prevent infinite loop by checking if we're already in the process of signing out
      if (isSigningOut.current) {
        console.log('🔒 Already signing out, skipping clearStaleSession to prevent loop');
        return;
      }

      // CRITICAL FIX: Set flag IMMEDIATELY before any async operations
      // This prevents race condition where multiple calls bypass the guard
      isSigningOut.current = true;
      console.log('🔒 Setting isSigningOut flag, starting clearStaleSession');

      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        // If there was an error getting the session, clear local storage anyway
        if (sessionError) {
          console.log('🔒 Session error detected, clearing local storage');
          localStorage.removeItem('user');
          window.dispatchEvent(new Event('userStateChanged'));
          isSigningOut.current = false; // Reset flag since we're done
          return;
        }

        // If there's a session but no active user in context, clear it
        // This handles cases where localStorage has stale session data
        // CRITICAL: Also check !loading to avoid clearing fresh sessions still being processed
        if (session && !user && !loading) {
          console.log('🔒 Stale session detected on auth page, clearing...', {
            hasSession: !!session,
            hasUser: !!user,
            loading: loading,
            sessionEmail: session?.user?.email
          });

          try {
            // Sign out via Supabase
            await supabase.auth.signOut();

            // CRITICAL: Force-clear ALL Supabase tokens immediately
            // This prevents getSession() from finding stale tokens on next check
            console.log('🔒 Force-clearing Supabase tokens from localStorage');
            Object.keys(localStorage).forEach(key => {
              if (key.startsWith('sb-')) {
                localStorage.removeItem(key);
                console.log(`🔒 Removed: ${key}`);
              }
            });

            // Clear our custom user data
            localStorage.removeItem('user');
            window.dispatchEvent(new Event('userStateChanged'));

            console.log('🔒 Stale session cleared successfully');
          } catch (signOutError: any) {
            // Silently handle session-related errors (session already gone)
            if (!signOutError?.message?.includes('session') && !signOutError?.message?.includes('Auth session missing')) {
              console.error('Error signing out stale session:', signOutError);
            }
            // Always clear local storage even if signOut fails
            localStorage.removeItem('user');
            window.dispatchEvent(new Event('userStateChanged'));
          } finally {
            // Reset flag after a delay to allow auth state to settle
            // This prevents re-triggering during the auth state change cascade
            setTimeout(() => {
              isSigningOut.current = false;
              console.log('🔒 Reset isSigningOut flag (after stale session clear)');
            }, 5000); // Extended to 5 seconds for extra safety
          }
        } else {
          // No stale session found - reset flag immediately and return
          console.log('🔒 No stale session found, resetting flag');
          isSigningOut.current = false;
          return;
        }
      } catch (error: any) {
        // Handle any unexpected errors gracefully
        if (!error?.message?.includes('session') && !error?.message?.includes('Auth session missing')) {
          console.error('Error clearing stale session:', error);
        }
        // Clear local storage as a fallback
        localStorage.removeItem('user');
        window.dispatchEvent(new Event('userStateChanged'));
        // CRITICAL: Reset flag on error to prevent permanent lock
        isSigningOut.current = false;
        console.log('🔒 Reset isSigningOut flag (after error)');
      }
    };

    // Only clear if user explicitly navigated to /auth (not during OAuth callback)
    // Check both query params (?code=) AND hash fragments (#access_token=)
    const hasOAuthCallback = window.location.search.includes('code=') ||
                             window.location.hash.includes('access_token=') ||
                             window.location.hash.includes('refresh_token=');

    // Store OAuth callback state in ref so it persists even after URL hash is cleared
    if (hasOAuthCallback) {
      console.log('🔐 OAuth callback detected, skipping session clear');
      isOAuthCallbackRef.current = true;
    }

    // Only clear session if:
    // 1. Not currently an OAuth callback in URL
    // 2. Not previously detected as OAuth callback (persists even after hash cleared)
    if (!hasOAuthCallback && !isOAuthCallbackRef.current) {
      clearStaleSession();
    } else if (isOAuthCallbackRef.current) {
      console.log('🔐 OAuth callback in progress (ref persisted), skipping session clear');
    }
  }, [user]); // Removed 'loading' from dependencies to prevent re-trigger during auth processing

  // Check for OAuth callback on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get('code');
    const error = queryParams.get('error');

    if (code || error) {
      setIsOAuthCallback(true);

      if (error) {
        const errorDescription = queryParams.get('error_description');
        setError(errorDescription || 'Authentication failed. Please try again.');
        setIsOAuthCallback(false);
        // Clean up URL
        window.history.replaceState(null, '', window.location.pathname);
      }
    }
  }, []);

  // Redirect if already authenticated (but not during OAuth callback)
  useEffect(() => {
    if (user && !loading && !isOAuthCallback) {
      navigate(from, { replace: true });
    }

    // If we have a user and we were processing OAuth, clear the flag and redirect
    if (user && !loading && isOAuthCallback) {
      setIsOAuthCallback(false);
      // Clean up URL
      window.history.replaceState(null, '', window.location.pathname);
      navigate(from, { replace: true });
    }
  }, [user, loading, isOAuthCallback, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // SECURITY: Check rate limit before attempting authentication
    const rateLimitCheck = checkRateLimit(email.trim().toLowerCase());
    if (rateLimitCheck.isBlocked) {
      setError(getRateLimitMessage(rateLimitCheck.remainingTime || 0));
      return;
    }

    // Show warning if approaching rate limit
    if (rateLimitCheck.attemptsLeft !== undefined && rateLimitCheck.attemptsLeft <= 2) {
      console.warn(`⚠️ Only ${rateLimitCheck.attemptsLeft} attempt(s) remaining before cooldown`);
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        await signIn(email.trim().toLowerCase(), password);
        // Record successful attempt to clear rate limit
        recordSuccessfulAttempt(email.trim().toLowerCase());
      } else {
        // SECURITY: Validate password strength
        const passwordValidation = validatePassword(password);
        if (!passwordValidation.isValid) {
          setError(passwordValidation.message || 'Password does not meet requirements.');
          setIsLoading(false);
          return;
        }

        // Validate password confirmation
        if (password !== confirmPassword) {
          setError('Passwords do not match. Please try again.');
          setIsLoading(false);
          return;
        }

        // Validate required agreements for signup
        if (!acceptedTerms || !acceptedPrivacy) {
          setError('Please accept the Terms of Service and Privacy Policy to continue.');
          setIsLoading(false);
          return;
        }

        await signUp(email.trim().toLowerCase(), password, name, phone);

        // Record successful signup to clear any rate limits
        recordSuccessfulAttempt(email.trim().toLowerCase());

        // User is now logged in instantly - useEffect will handle redirect
      }

      // The useEffect hook will handle the redirect after user state updates
    } catch (error: any) {
      // DEBUGGING: Log full error details to console
      console.error('🚨 Auth error details:', {
        message: error.message,
        code: error.code,
        status: error.status,
        fullError: error
      });

      // SECURITY: Record failed attempt for rate limiting
      // Only record for login failures and duplicate signups (actual auth failures)
      if (
        error.message?.includes('Invalid login credentials') ||
        error.message?.includes('Email not confirmed') ||
        error.message?.includes('User already registered')
      ) {
        recordFailedAttempt(email.trim().toLowerCase());
      }

      // Parse error and show user-friendly message
      let errorMessage = '';

      if (error.message?.includes('Invalid login credentials')) {
        errorMessage = 'Invalid email or password. Please try again.';
      } else if (error.message?.includes('Email not confirmed')) {
        errorMessage = 'Please verify your email before logging in.';
      } else if (error.message?.includes('User already registered')) {
        errorMessage = 'An account with this email already exists.';
      } else if (error.message?.includes('Password should be at least 6 characters') || error.message?.includes('AuthWeakPasswordError')) {
        errorMessage = 'Password should be at least 6 characters.';
      } else if (error.message?.includes('rate limit') || error.message?.includes('Rate limit')) {
        errorMessage = 'Email service rate limit reached. Please try again in a few minutes.';
      } else if (error.message?.includes('SMTP') || error.message?.includes('Email service')) {
        errorMessage = 'Email service configuration error. Please contact support.';
      } else if (error.message?.includes('invalid_grant')) {
        errorMessage = 'Authentication failed. Please try again.';
      } else if (error.message?.toLowerCase().includes('invalid') && error.message?.toLowerCase().includes('email')) {
        // Only show "invalid email" for actual email format errors
        errorMessage = 'Please enter a valid email address.';
      } else {
        // Show the actual error message for debugging, or a generic message
        errorMessage = error.message || (isLogin ? 'Failed to log in. Please try again.' : 'Failed to create account. Please try again.');
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setIsLoading(true);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
        },
      });

      if (error) throw error;

      // OAuth redirect will happen automatically
      // No need to set loading to false as page will redirect
    } catch (error: any) {
      console.error('Google sign in error:', error);

      let errorMessage = 'Failed to sign in with Google. Please try again.';

      if (error.message?.includes('popup')) {
        errorMessage = 'Popup was blocked. Please allow popups and try again.';
      } else if (error.message?.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };


  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setPhone('');
    setAcceptedTerms(false);
    setAcceptedPrivacy(false);
  };

  const openPolicyModal = (type: 'privacy' | 'terms') => {
    setPolicyModalType(type);
    setShowPolicyModal(true);
  };

  const handleForgotPassword = async () => {
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);

    // Validate email
    if (!forgotPasswordEmail || !forgotPasswordEmail.includes('@')) {
      setForgotPasswordError('Please enter a valid email address.');
      return;
    }

    setForgotPasswordLoading(true);

    try {
      await resetPassword(forgotPasswordEmail.trim().toLowerCase());
      setForgotPasswordSuccess(true);
      setForgotPasswordEmail('');
    } catch (error: any) {
      console.error('Password reset error:', error);

      let errorMessage = 'Failed to send password reset email. Please try again.';

      if (error.message?.includes('rate limit') || error.message?.includes('Rate limit')) {
        errorMessage = 'Too many requests. Please try again in a few minutes.';
      } else if (error.message?.includes('Email not found')) {
        // For security, we don't want to reveal if an email exists or not
        // So we'll show success message anyway
        setForgotPasswordSuccess(true);
        setForgotPasswordEmail('');
        setForgotPasswordLoading(false);
        return;
      }

      setForgotPasswordError(errorMessage);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const closeForgotPasswordModal = () => {
    setShowForgotPasswordModal(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setForgotPasswordSuccess(false);
  };

  // Show loading state during OAuth callback
  if (isOAuthCallback) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neon-green mx-auto mb-4"></div>
          <p className="text-white text-lg font-semibold mb-2">Completing sign in...</p>
          <p className="text-gray-400 text-sm">Setting up your account. This may take a moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-neon-green/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-neon-blue/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md space-y-8">
        {/* Logo and Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <motion.div
            className="w-20 h-20 bg-gradient-to-br from-neon-green to-neon-blue rounded-xl flex items-center justify-center mx-auto mb-6 overflow-hidden"
            whileHover={{ rotate: 360 }}
            transition={{ duration: 0.6 }}
          >
            <img
              src="/images/picksCity.png"
              alt="PicksCity Logo"
              className="w-full h-full object-contain"
            />
          </motion.div>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-futuristic mb-2">
            Welcome to PicksCity
          </h1>
          <p className="text-gray-400 text-lg">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </p>
        </motion.div>

        {/* Auth Mode Toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex bg-dark-800/60 rounded-lg p-1"
        >
          <button
            type="button"
            onClick={() => setIsLogin(true)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              isLogin
                ? 'bg-neon-green text-black'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => setIsLogin(false)}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              !isLogin
                ? 'bg-neon-green text-black'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Sign Up
          </button>
        </motion.div>

        {/* Google Sign-In - Recommended */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="w-full px-4 py-3 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors font-semibold flex items-center justify-center space-x-2 relative"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Sign {isLogin ? 'in' : 'up'} with Google</span>
            <span className="absolute -top-2 -right-2 bg-neon-green text-black text-xs font-bold px-2 py-1 rounded-full">
              Recommended
            </span>
          </button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-dark-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-dark-900 text-gray-400">Or continue with email</span>
            </div>
          </div>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-6"
          onSubmit={handleSubmit}
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center space-x-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-sm text-red-400">{error}</p>
            </motion.div>
          )}

          <div className="space-y-4">
            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
                  Full Name
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required={!isLogin}
                    className="w-full pl-10 pr-4 py-3 bg-dark-800 text-white rounded-lg border border-dark-600 focus:border-neon-green focus:outline-none transition-colors"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
              </motion.div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full pl-10 pr-4 py-3 bg-dark-800 text-white rounded-lg border border-dark-600 focus:border-neon-green focus:outline-none transition-colors"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-400 mb-2">
                Password {!isLogin && <span className="text-xs text-gray-500">(min 8 chars, uppercase, lowercase, number, special char)</span>}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  required
                  className="w-full pl-10 pr-12 py-3 bg-dark-800 text-white rounded-lg border border-dark-600 focus:border-neon-green focus:outline-none transition-colors"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors p-2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {/* Password strength indicator */}
              {!isLogin && password && passwordStrength && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 bg-dark-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        passwordStrength === 'weak' ? 'w-1/3 bg-red-500' :
                        passwordStrength === 'medium' ? 'w-2/3 bg-yellow-500' :
                        'w-full bg-green-500'
                      }`}
                    />
                  </div>
                  <span className={`text-xs font-medium ${
                    passwordStrength === 'weak' ? 'text-red-400' :
                    passwordStrength === 'medium' ? 'text-yellow-400' :
                    'text-green-400'
                  }`}>
                    {passwordStrength === 'weak' ? 'Weak' : passwordStrength === 'medium' ? 'Medium' : 'Strong'}
                  </span>
                </div>
              )}
            </div>

            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-400 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required={!isLogin}
                    className="w-full pl-10 pr-12 py-3 bg-dark-800 text-white rounded-lg border border-dark-600 focus:border-neon-green focus:outline-none transition-colors"
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors p-2"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </motion.div>
            )}

            {!isLogin && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-400 mb-2">
                    Phone Number (Optional)
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className="w-full px-4 py-3 bg-dark-800 text-white rounded-lg border border-dark-600 focus:border-neon-green focus:outline-none transition-colors"
                    placeholder="Enter your phone number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>

                {/* Terms and Privacy Policy Agreement */}
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <input
                      id="accept-terms"
                      type="checkbox"
                      checked={acceptedTerms}
                      onChange={(e) => setAcceptedTerms(e.target.checked)}
                      className="mt-1 h-4 w-4 bg-dark-800 border-dark-600 rounded text-neon-green focus:ring-neon-green"
                    />
                    <label htmlFor="accept-terms" className="text-sm text-gray-400 leading-relaxed">
                      I accept the{' '}
                      <button
                        type="button"
                        onClick={() => openPolicyModal('terms')}
                        className="text-neon-green hover:text-green-400 transition-colors underline"
                      >
                        Terms of Service
                      </button>
                      <span className="text-red-400 ml-1">*</span>
                    </label>
                  </div>

                  <div className="flex items-start space-x-3">
                    <input
                      id="accept-privacy"
                      type="checkbox"
                      checked={acceptedPrivacy}
                      onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                      className="mt-1 h-4 w-4 bg-dark-800 border-dark-600 rounded text-neon-green focus:ring-neon-green"
                    />
                    <label htmlFor="accept-privacy" className="text-sm text-gray-400 leading-relaxed">
                      I accept the{' '}
                      <button
                        type="button"
                        onClick={() => openPolicyModal('privacy')}
                        className="text-neon-green hover:text-green-400 transition-colors underline"
                      >
                        Privacy Policy
                      </button>
                      <span className="text-red-400 ml-1">*</span>
                    </label>
                  </div>

                  <p className="text-xs text-gray-500">
                    <span className="text-red-400">*</span> Required fields
                  </p>
                </div>
              </motion.div>
            )}
          </div>

          {isLogin && (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 bg-dark-800 border-dark-600 rounded text-neon-green focus:ring-neon-green"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-400">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setShowForgotPasswordModal(true)}
                  className="text-neon-green hover:text-green-400 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-3 bg-neon-green text-black rounded-lg hover:bg-green-400 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                {isLogin ? 'Sign In' : 'Create Account'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </>
            )}
          </button>

          {/* Toggle Auth Mode */}
          <div className="text-center mt-6">
            <span className="text-gray-400">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
            </span>
            <button
              type="button"
              onClick={toggleAuthMode}
              className="text-neon-green hover:text-green-400 transition-colors font-semibold"
            >
              {isLogin ? 'Sign Up' : 'Sign In'}
            </button>
          </div>
        </motion.form>
      </div>

      {/* Policy Modal */}
      <PolicyModal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        type={policyModalType}
      />

      {/* Forgot Password Modal */}
      <AnimatePresence>
        {showForgotPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeForgotPasswordModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-dark-800 rounded-xl shadow-2xl max-w-md w-full p-6 border border-dark-600"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-white mb-2">Reset Password</h2>
              <p className="text-gray-400 text-sm mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              {forgotPasswordSuccess ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 flex items-start space-x-3">
                    <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                      <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-green-400 font-medium mb-1">
                        Check your email!
                      </p>
                      <p className="text-sm text-gray-300">
                        If an account exists with that email, we've sent you a password reset link. Please check your inbox and spam folder.
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={closeForgotPasswordModal}
                    className="w-full px-4 py-3 bg-neon-green text-black rounded-lg hover:bg-green-400 transition-colors font-semibold"
                  >
                    Close
                  </button>
                </motion.div>
              ) : (
                <div className="space-y-4">
                  {forgotPasswordError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-center space-x-3"
                    >
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-400">{forgotPasswordError}</p>
                    </motion.div>
                  )}

                  <div>
                    <label htmlFor="forgot-password-email" className="block text-sm font-medium text-gray-400 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        id="forgot-password-email"
                        type="email"
                        autoComplete="email"
                        className="w-full pl-10 pr-4 py-3 bg-dark-700 text-white rounded-lg border border-dark-600 focus:border-neon-green focus:outline-none transition-colors"
                        placeholder="Enter your email"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleForgotPassword();
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={closeForgotPasswordModal}
                      className="flex-1 px-4 py-3 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors font-semibold"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={forgotPasswordLoading}
                      className="flex-1 px-4 py-3 bg-neon-green text-black rounded-lg hover:bg-green-400 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {forgotPasswordLoading ? (
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : (
                        'Send Reset Link'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AuthLanding;